import React, { useState, useEffect, useRef } from "react";
import style from "./AdminListToolbar.module.scss";
import { InputField } from "../../primary/InputField";
import SearchIcon from "../../../static/images/search-icon.svg";
import PlusIcon from "../../../static/images/plus-icon_white.svg";
import DeleteIcon from "../../../static/images/delete-icon.svg";

interface AdminListToolbarProps {
  searchPlaceholder?: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onSearch: (value: string) => void;
  selectedCount?: number;
  onDeleteSelected?: () => void;
  canDelete?: boolean;
  onCreateClick?: () => void;
  createLabel?: string;
  canCreate?: boolean;
  className?: string;
}

/**
 * AdminListToolbar - standard toolbar row for admin list pages.
 *
 * Provides search (InputField with search-icon prefix and clear-X), a "selected N"
 * count, a Delete button, and a primary Create button. All slots are optional so
 * the toolbar can be trimmed to whatever a specific screen needs.
 *
 * Search pattern: the toolbar owns the live-typing state (`inputValue`) so the
 * input is always responsive. The parent's `searchValue` prop is used only for
 * external sync (e.g. after a programmatic clear). Search is committed on Enter,
 * not on every keystroke - keeping query traffic low.
 */
export const AdminListToolbar: React.FC<AdminListToolbarProps> = ({
  searchPlaceholder = "Search…",
  searchValue,
  onSearchChange,
  onSearch,
  selectedCount = 0,
  onDeleteSelected,
  canDelete = true,
  onCreateClick,
  createLabel = "Create",
  canCreate = true,
  className,
}) => {
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [inputValue, setInputValue] = useState(searchValue);

  useEffect(() => {
    setInputValue(searchValue);
  }, [searchValue]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    if (val === "") {
      onSearchChange("");
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      onSearch(inputValue);
    }
  };

  const handleClear = () => {
    setInputValue("");
    onSearchChange("");
  };

  const searchPrefix = (
    <img src={SearchIcon} alt="" aria-hidden="true" className={style.searchIcon} onClick={() => searchInputRef.current?.focus()} />
  );

  return (
    <div className={`${style.toolbar}${className ? ` ${className}` : ""}`}>
      {/* Search */}
      <div className={style.searchArea}>
        <InputField
          id="adminListSearch"
          name="search"
          dataTestId="search"
          placeholder={searchPlaceholder}
          value={inputValue}
          onChange={handleChange}
          onKeyUp={handleKeyUp}
          showClear={!!inputValue}
          onClear={handleClear}
          prefix={searchPrefix}
          inputRef={searchInputRef}
        />
      </div>

      {/* Actions: selected count + delete + create */}
      <div className={style.actionsArea}>
        {selectedCount > 0 && (
          <span id="selectedRowCount" className={style.selectedCount}>
            {selectedCount} Selected
          </span>
        )}

        {selectedCount > 0 && onDeleteSelected && canDelete && (
          <button type="button" className={style.btnDelete} onClick={onDeleteSelected} data-testid="delete-selected-btn">
            <img src={DeleteIcon} alt="" aria-hidden="true" />
            Delete
          </button>
        )}

        {onCreateClick && (
          <button type="button" className={style.btnCreate} onClick={onCreateClick} disabled={!canCreate} data-testid="create-btn">
            <img src={PlusIcon} alt="" aria-hidden="true" className={style.plusIcon} />
            {createLabel}
          </button>
        )}
      </div>
    </div>
  );
};
